import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Thème choisi par l'utilisateur.
 *
 * `system` n'est pas un troisième look : c'est l'ABSENCE de choix explicite, on
 * suit alors le réglage de l'appareil (`prefers-color-scheme`) et l'app bascule
 * toute seule au coucher du soleil sur les téléphones qui le font. C'est la
 * valeur par défaut : décider à la place de l'utilisateur avant qu'il n'ait
 * touché à quoi que ce soit serait présomptueux.
 */
export type ThemePreference = "light" | "dark" | "system";

type PreferencesState = {
  /** Les montants du wallet sont-ils masqués (••••) ? */
  hideAmounts: boolean;
  toggleHideAmounts: () => void;
  setHideAmounts: (hidden: boolean) => void;

  /**
   * La Home a-t-elle déjà été ouverte au moins une fois ?
   *
   * Sert à choisir le message d'accueil : « Bienvenue » à la 1ʳᵉ visite,
   * « Content de vous revoir » ensuite.
   *
   * ⚠️ Distinct de `hasCompletedOnboarding` du
   * sessionStore : ces deux-là passent à `true` PENDANT le premier parcours
   * d'auth, donc ils sont déjà vrais quand la Home s'affiche pour la 1ʳᵉ fois —
   * ils ne peuvent pas départager les deux messages.
   */
  hasVisitedHome: boolean;
  markHomeVisited: () => void;

  /** Choix de l'utilisateur — `system` tant qu'il n'a rien décidé. */
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
  /**
   * Bascule clair ⇄ sombre.
   *
   * Depuis `system`, on part du thème RÉELLEMENT affiché pour aller à son
   * opposé : appuyer sur l'interrupteur alors que l'appareil est en sombre doit
   * éclairer, jamais assombrir davantage.
   */
  toggleTheme: () => void;
};

/**
 * Préférences d'AFFICHAGE de l'utilisateur, persistées entre les lancements.
 *
 * ## Pourquoi un store séparé de `sessionStore`
 * `sessionStore.logout()` remet tout son état à zéro. Une préférence d'affichage
 * doit SURVIVRE à une déconnexion : quelqu'un qui masque ses montants pour ne
 * pas les exposer en public ne veut pas les voir réapparaître après un
 * re-login. Ces réglages ne sont pas non plus des données de compte — ils
 * restent locaux à l'appareil.
 *
 * ⚠️ Rien de sensible ici : `localStorage` n'est pas chiffré et reste lisible
 * par tout script de l'origine. On n'y stocke qu'un booléen de confort, jamais
 * un montant ni un identifiant.
 *
 * Port web : `localStorage` remplace AsyncStorage. Contrairement aux tokens
 * (sessionStorage, cf. api/secureTokens.ts), ces préférences DOIVENT survivre à
 * la fermeture de l'onglet — c'est tout leur intérêt.
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      // Visible par défaut : masquer d'entrée ferait croire à un solde vide ou
      // à un écran cassé pour qui n'a jamais touché au bouton œil.
      hideAmounts: false,
      toggleHideAmounts: () => set((s) => ({ hideAmounts: !s.hideAmounts })),
      setHideAmounts: (hidden) => set({ hideAmounts: hidden }),

      hasVisitedHome: false,
      // Idempotent : on n'écrit QUE lors du basculement. Sans cette garde, chaque
      // rendu de la Home déclencherait une écriture localStorage inutile.
      markHomeVisited: () =>
        set((s) => (s.hasVisitedHome ? s : { ...s, hasVisitedHome: true })),

      theme: "system",
      setTheme: (theme) => set({ theme }),
      toggleTheme: () =>
        set((s) => ({ theme: resolveTheme(s.theme) === "dark" ? "light" : "dark" })),
    }),
    {
      name: "alomain-preferences",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/**
 * Thème RÉELLEMENT affiché : résout `system` en interrogeant l'appareil.
 *
 * Hors navigateur (tests, rendu serveur), on retombe sur le clair — le thème de
 * référence de l'app, et celui de toutes les captures du design.
 */
export function resolveTheme(preference: ThemePreference): "light" | "dark" {
  if (preference !== "system") return preference;
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
